import {
    DefaultButton, IDropdownOption,
    IPanelProps,
    IRenderFunction,
    IStackProps,
    Panel,
    PanelType,
    PivotItem,
    PrimaryButton, Separator,
    Stack, useTheme
} from '@fluentui/react';
import { RootState } from 'app/store';
import CESDropdown from 'components/controls/CESDropdown';
import ControlMask from 'components/controls/ControlMask';
import DropdownControl from 'components/controls/DropdownControl';
import LookupControlBase from 'components/controls/LookupControlBase';
import TextFieldControl from 'components/controls/TextFieldControl';
import ToggleControl from 'components/controls/ToggleControl';
import { useApplication } from 'global/Application/Context';
import { ArrayToMapByPredicateWithMap, addNMonthsToDateString } from 'global/Utility/TSFunctions';
import { IFieldReference } from 'global/Visibility/IFieldReference';
import * as FAM from 'model/FlexibleAttributeModel';
import { IGroupConfigurationData } from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { CancellationFieldsByCategory, EViewType, fadeIn, panelButtonsStyles } from "../../global/Application";
import AccountsReceivableWizard from './AccountsReceivableWizard';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import PlanWizard from './PlanWizard';
import StopLossWizard from './StopLossWizard';
import SectionItem, { ESectionItemVariant } from 'components/controls/SectionItem/Index';

interface IProps extends TComponentConnectedProps {
    onPick?: (index: number) => void;
    snap?: IGroupConfigurationData;

    // if provided, filters display grid to only referenced for this plan
    currentPlan?: FAM.IPlan;

    // if provided, filters display grid to only referenced for this accounts receivable
    currentAR?: FAM.IAccountsReceivable;

    disabled?: boolean;

    onChange?: (groupNumber: FAM.IGroupNumber | null) => void;

    defaultValue?: FAM.IGroupNumber;

    isOpen: boolean;

    onDismiss?: () => void;


}

const GroupStructureGroupNumberView = (props: IProps) => {

    const theme = useTheme();

    const application = useApplication();

    // allows the component to perform a local visibility management, to help with the animation and ui smoothness
    // by default this should always be visible unless components has signaled a context job termination and wants to self close
    const [selfVisible, setSelfVisible] = useState(true);

    const [groupNumber, setGroupNumber] = useState<FAM.IGroupNumber | null>(props.defaultValue
        ? props.defaultValue
        : null
    );

    useEffect(() => {
        if (props.isOpen) {
            setSelfVisible(true);

            if (props.defaultValue)
                setGroupNumber(t => {
                    const value = structuredClone(props.defaultValue)!;

                    if (value?.id === 'bulk') {

                        //@ts-ignore
                        delete value.cesNumberRef;

                        //@ts-ignore
                        delete value.deactivated;

                        //@ts-ignore
                        delete value.status;
                    }

                    return value
                });
        }

    }, [props.isOpen, props.defaultValue])

    const categoryGroupStructure = props.state.data.config.cache.map_bcbs_config_category_named['Group Number'];

    const statusOptions = useMemo(() => {
        return props.state.data.config.cache.options_benefitStatusReasons;
    }, []);

    const isBulk = groupNumber?.id === 'bulk';

    const [isPlanOpen, setIsPlanOpen] = useState(false);
    const [isAROpen, setIsAROpen] = useState(false);
    const [isStopLossOpen, setIsStopLossOpen] = useState(false);

    const onDismiss = () => {
        props.onDismiss?.();
    }

    function calcGroupNumber(prefix: string | null | undefined, base: string | null | undefined, suffix: string | null | undefined) {

        return `${prefix ?? ''}-${base ?? ''}-${suffix ?? ''}`;
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <div>
            <Separator />

            <PlanWizard

                isOpen={isPlanOpen}
                onDismiss={() => { setIsPlanOpen(false) }}
                onCancel={() => { setIsPlanOpen(false) }}
                onConfirm={(event) => {
                    setGroupNumber(t => {
                        const n = { ...t! };

                        n.planRef = event?.value?.planSelected
                            ? {
                                id: event.value.planSelected.id!,
                                name: event.value.planSelected.name!
                            }
                            : undefined;

                        return n;
                    });
                }}
                variant="pick"
            />

            <AccountsReceivableWizard
                isOpen={isAROpen}
                onDismiss={() => { setIsAROpen(false); }}
                onCancel={() => { setIsAROpen(false); }}
                variant="pick"
                onConfirm={(event) => {

                    debugger;

                    setGroupNumber(t => {
                        const n = { ...t! };

                        n.accountsReceivableRef = event?.value?.ARSelected
                            ? {
                                id: event.value.ARSelected.id!,
                                name: event.value.ARSelected.name!
                            }
                            : undefined;

                        return n;
                    });

                    setIsAROpen(false);
                }}
            />

            <StopLossWizard
                isOpen={isStopLossOpen}
                onDismiss={() => { setIsStopLossOpen(false); }}
                onCancel={() => { setIsStopLossOpen(false); }}
                variant="pick"
                onConfirm={(event) => {

                    setGroupNumber(t => {
                        const n = { ...t! };

                        n.stopLossRef = event?.value.stopLossSelected
                            ? {
                                id: event.value.stopLossSelected.id!,
                                name: event.value.stopLossSelected.name!
                            }
                            : undefined;

                        return n;
                    });

                    setIsStopLossOpen(false);
                }}
            />

            <FlexibleFieldView
                disabled={props.state.status.viewType === EViewType.ViewOnly || props.disabled}
                fields={groupNumber?.fields!}
                category={categoryGroupStructure}
                tiers={[]}
                bulk={groupNumber?.id === 'bulk'}
                cancellation={groupNumber?.status?.value === FAM.EBenefitStatus.Cancelled}
                scope={groupNumber}
                scopeType={EScopeType.groupNumber}
                onChange={(changeEvent) => {
                    if (changeEvent.field)
                        setGroupNumber(xGroupNumber => {
                            const nGroupNumber = { ...xGroupNumber! };

                            const fields = nGroupNumber.fields!;
                            const field = nGroupNumber.fields?.find(q => q.id === changeEvent.field!.id)!;
                            if (field && field?.values?.[0])
                                field.values[0].value = changeEvent.value!;

                            const _fieldMap = ArrayToMapByPredicateWithMap(fields, eField => eField.configRef.id, (eField, index) => {
                                const attribute = props.state.data.config.cache.map_bcbs_config_attribute[eField.configRef.id];
                                const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                                const ret: IFieldReference = {
                                    index: index,
                                    field: eField,
                                    attribute: attribute,
                                    masterAttribute: masterAttribute,
                                }
                                return ret;
                            });

                            const changeValue = changeEvent.value;
                            
                            const cancellationFields = CancellationFieldsByCategory['23153cda-d08d-ec11-b400-0022480b6b1d'];

                            const {
                                "Cancellation Effective Date": cancellationEffectiveDateId,
                                "Pay Run Out Claims How Many Months": payRunOutClaimsHowManyMonthsId,
                                "Run Out Claims End Date": runOutClaimsEndDateId,
                            } = cancellationFields ?? {};

                            // Auto-calculate Run Out Claims End Date
                            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/377166
                            // Benefit Level
                            if (field.configRef.id === cancellationEffectiveDateId || field.configRef.id === payRunOutClaimsHowManyMonthsId) {
                                if (changeValue) {
                                    // Cancellation Effective Date
                                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c9a8fddf-52a3-ec11-9840-0022480b1a5e
                                    let bcbs_cancellationeffectivedate = null as null | string;
                                    const bcbs_cancellationeffectivedate_fieldMap = _fieldMap.get(cancellationEffectiveDateId);
                                    if (bcbs_cancellationeffectivedate_fieldMap?.field.values?.[0].value?.type === 'date')
                                        bcbs_cancellationeffectivedate = bcbs_cancellationeffectivedate_fieldMap.field.values[0].value.value;

                                    // Group Configuration: if this is root, use effective date configuration
                                    if (cancellationEffectiveDateId === '36a46909-d05d-ec11-8f8f-000d3a5a062b')
                                        bcbs_cancellationeffectivedate = props.state.data.json.effectiveDate?.value!

                                    // Pay Run Out Claims How Many Months
                                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=cda8fddf-52a3-ec11-9840-0022480b1a5e
                                    const bcbs_payrunoutclaimsmonths_fieldMap = _fieldMap.get(payRunOutClaimsHowManyMonthsId);
                                    let bcbs_payrunoutclaimsmonths = null as null | number;
                                    if (bcbs_payrunoutclaimsmonths_fieldMap?.field.values?.[0].value?.type === 'number')
                                        bcbs_payrunoutclaimsmonths = bcbs_payrunoutclaimsmonths_fieldMap?.field.values[0].value.value;

                                    // Run Out Claims End Date
                                    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=cfa8fddf-52a3-ec11-9840-0022480b1a5e
                                    const bcbs_runoutclaimsenddate_fieldMap = _fieldMap.get(runOutClaimsEndDateId);

                                    if (bcbs_cancellationeffectivedate && bcbs_payrunoutclaimsmonths && bcbs_runoutclaimsenddate_fieldMap) {
                                        const dateAPlusNMonths = addNMonthsToDateString(bcbs_cancellationeffectivedate, bcbs_payrunoutclaimsmonths);
                                        const value: FAM.TValue = { type: 'date', value: dateAPlusNMonths };
                                        bcbs_runoutclaimsenddate_fieldMap.field.values[0].value = value
                                    }
                                }
                            }



                            return nGroupNumber;
                        });
                }}
                firstPivotItem={onRenderProperties}
            />

        </div>
    }


    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!groupNumber)
            return null;

        return <PivotItem
            headerText={"Properties"}
            key={"groupNumberProperties"}
            itemKey={"groupNumberProperties"}
        >
            <SectionItem
                title="General"
                variant={ESectionItemVariant.Drawer}
            >
                <ControlMask
                    label="CES Client Number"
                    defaultLocked={isBulk}
                >
                    <CESDropdown
                        value={groupNumber?.cesNumberRef ? { id: groupNumber.cesNumberRef.id, cesNumber: groupNumber.cesNumberRef.name, deactivated: false } : undefined}
                        onChange={(option) => {
                            setGroupNumber(t => { return { ...t!, cesNumberRef: { id: option.id, name: option.cesNumber } } });
                        }}
                    />
                </ControlMask>

                <TextFieldControl
                    label={"Prefix"}
                    disabled={isBulk || props.disabled}
                    fieldValueOnChange={(event, value) => {

                        setGroupNumber(t => {
                            return {
                                ...t!,
                                prefix: value.value ?? "",
                                groupNumber: calcGroupNumber(value.value ?? "", t?.base, t?.suffix)
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: groupNumber?.prefix ?? '' }}
                />

                <TextFieldControl
                    label={"Base"}
                    disabled={isBulk || props.disabled}
                    fieldValueOnChange={(event, value) => {

                        setGroupNumber(t => {
                            return {
                                ...t!,
                                base: value.value ?? "",
                                groupNumber: calcGroupNumber(t?.prefix, value.value ?? "", t?.suffix)
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: groupNumber?.base ?? '' }}
                />


                <TextFieldControl
                    label={"Suffix"}
                    disabled={isBulk || props.disabled}
                    fieldValueOnChange={(event, value) => {

                        setGroupNumber(t => {
                            return {
                                ...t!,
                                suffix: value.value ?? "",
                                groupNumber: calcGroupNumber(t?.prefix, t?.base, value.value ?? "")
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: groupNumber?.suffix ?? '' }}
                />


                <TextFieldControl
                    label={"Group Number"}
                    disabled={true}
                    defaultFieldValue={{
                        type: 'string',
                        value: groupNumber.groupNumber ?? ''
                    }}
                />

                <LookupControlBase
                    label='Plan'
                    onLookup={() => { setIsPlanOpen(true) }}
                    onDelete={() => {
                        setGroupNumber(t => {
                            return { ...t!, planRef: null };
                        });
                    }}
                    value={groupNumber?.planRef?.name}
                    disabled={props.disabled || !application.ChangeTypePermissions.canLinkGroupStructurePlan}
                />

                <LookupControlBase
                    label='Accounts Receivable'
                    onLookup={() => { setIsAROpen(true) }}
                    onDelete={() => {
                        setGroupNumber(t => {
                            return { ...t!, accountsReceivableRef: null };
                        });
                    }}
                    value={groupNumber?.accountsReceivableRef?.name}
                    disabled={props.disabled || !application.ChangeTypePermissions.canLinkGroupStructureAccountReceivable}
                />

                <LookupControlBase
                    label='Stop Loss'
                    onLookup={() => { setIsStopLossOpen(true) }}
                    onDelete={() => {
                        setGroupNumber(t => {
                            return { ...t!, stopLossRef: null };
                        });
                    }}
                    value={groupNumber?.stopLossRef?.name}
                    disabled={props.disabled || !application.ChangeTypePermissions.canLinkGroupStructureStopLoss}
                />

                <ControlMask
                    label="Status"
                    defaultLocked={isBulk}
                >
                    <DropdownControl
                        label={"Status"}
                        options={statusOptions}
                        disabled={props.disabled}
                        selectedKey={groupNumber?.status?.value}
                        defaultValue={groupNumber?.status?.name}
                        onChange={(_, option?: IDropdownOption<FAM.TBenefitStatusOptionSetValue>) => {
                            setGroupNumber(t => { return { ...t!, status: option?.data! }; });
                        }}
                    />
                </ControlMask>

                <ControlMask
                    label="Deactivate"
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setGroupNumber(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"Deactivate"}
                        checked={groupNumber.deactivated ?? false}
                        disabled={props.disabled || !application.ChangeTypePermissions.canDeactivateGroupStructure}
                        onChange={(_, checked) => {
                            setGroupNumber(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />
                </ControlMask>

            </SectionItem>

        </PivotItem>
    }

    const onChange = (value: FAM.IGroupNumber) => {
        props.onChange?.(value);
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton text='OK'
                styles={panelButtonsStyles}
                disabled={(!isBulk && (!groupNumber?.cesNumberRef?.name || !groupNumber.prefix || !groupNumber.base || !groupNumber.suffix)) || props.disabled}
                onClick={async (event) => {
                    setSelfVisible(false);

                    setTimeout(() => {
                        if (groupNumber)
                            onChange(groupNumber);
                    }, 300);

                }}
            />
            <DefaultButton text="Cancel"
                styles={panelButtonsStyles}
                onClick={onDismiss}
            />
        </div>
    }

    // const onDetailListSelect = (selectedItems: FAM.IGroupNumber[]) => {
    //     if (selectedItems && selectedItems.length === 1) {
    //         // single items selected for edit?
    //         // lets only pick that single item and display for edit

    //         const item = selectedItems[0];
    //         setValue(structuredClone(item));

    //     } else if (selectedItems && selectedItems.length > 1) {
    //         // multiple items selected, lets create a a new blank template and use that for batch edit
    //         // later what ever changes that are done to this model we will cascade to selected items.

    //         const groupNumber: FAM.IGroupNumber = {
    //             id: 'batchEdit',
    //             cesNumberRef: null,
    //             prefix: null,
    //             suffix: null,
    //             groupNumber: null,
    //             base: null,
    //             fields: createDefaultCategoryFields(props.state.data.config, categoryGroupStructure, 'Group Structure', []),
    //             deactivated: false,
    //         };

    //         setValue(groupNumber);
    //     }
    // }

    return (
        <Panel
            isOpen={props.isOpen && selfVisible}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Edit"
            isFooterAtBottom={true}
            onRenderFooterContent={onRenderFooter}
        >
            {onRenderBody()}
        </Panel>

    );
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupStructureGroupNumberView);
